import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=70&auto=format&fit=crop";

const Card = ({ p }) => (
  <Link
    to={`/blog/${p.slug}`}
    data-testid={`blog-card-${p.slug}`}
    className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition hover:shadow-lg"
  >
    <div className="relative aspect-[16/10] overflow-hidden">
      <img
        src={p.hero_image_url || HERO_FALLBACK}
        alt={p.hero_image_alt || p.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="flex flex-1 flex-col p-5">
      <div className="mb-2 flex flex-wrap gap-1 text-[11px]">
        {(p.tags || []).slice(0, 3).map((t) => (
          <span key={t} className="rounded-full bg-brandblue-light px-2 py-0.5 font-bold text-brandblue">{t}</span>
        ))}
      </div>
      <h3 className="font-display text-lg font-bold leading-snug text-navy group-hover:text-brandblue">
        {p.title}
      </h3>
      {p.subtitle && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{p.subtitle}</p>}
      <div className="mt-auto flex items-center gap-3 pt-4 text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.read_minutes || 3} min</span>
        {p.city && <span>· {p.city}</span>}
        <span className="ml-auto">{new Date(p.published_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}</span>
      </div>
    </div>
  </Link>
);

const Blog = () => {
  const [sp, setSp] = useSearchParams();
  const tag = sp.get("tag") || "";
  const city = sp.get("city") || "";
  const [data, setData] = useState(null);

  useSeo({
    title: tag ? `${tag} · WoningAlert Blog` : "Blog — huurtips, koopgids en scam-alerts | WoningAlert",
    description: "Praktische tips voor huurders en kopers in Nederland. Herken scams, versla concurrentie, en vind sneller een woning.",
  });

  useEffect(() => {
    setData(null);
    const q = new URLSearchParams({ limit: "24" });
    if (tag) q.set("tag", tag);
    if (city) q.set("city", city);
    api(`/blog?${q}`).then(setData).catch(() => setData({ posts: [], facets: { tags: [], cities: [] } }));
  }, [tag, city]);

  const featured = data?.posts?.[0];
  const rest = data?.posts?.slice(1) || [];

  const setFilter = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-navy">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70 ring-1 ring-white/15">
            WoningAlert Blog
          </span>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
            {tag ? `Artikelen over ${tag}` : "Slimmer huren en kopen in Nederland"}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/70">
            Actuele markttrends, buurtgidsen en praktische tips. Alles geschreven door redacteuren met échte ervaring in de Nederlandse woningmarkt.
          </p>
        </div>
      </div>

      {/* Filters */}
      {data?.facets && (data.facets.tags.length > 0 || data.facets.cities.length > 0) && (
        <div className="border-b border-border bg-white">
          <div className="mx-auto flex max-w-6xl flex-wrap gap-2 px-4 py-3 sm:px-6">
            {(tag || city) && (
              <button onClick={() => setSp(new URLSearchParams())} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-200">Alle</button>
            )}
            {data.facets.tags.map((t) => (
              <button key={t} onClick={() => setFilter("tag", tag === t ? "" : t)}
                data-testid={`filter-tag-${t}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${tag === t ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                #{t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {data === null ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" size={22} /></div>
        ) : !data.posts?.length ? (
          <div className="rounded-3xl bg-white p-10 text-center shadow-sm">
            <p className="font-display text-lg font-bold text-navy">Nog geen artikelen</p>
            <p className="mt-2 text-sm text-slate-500">Kom binnenkort terug — we werken aan de eerste posts.</p>
          </div>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} data-testid="blog-featured" className="group grid overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition hover:shadow-xl md:grid-cols-2">
                <div className="relative aspect-[16/10] md:aspect-auto md:h-full">
                  <img src={featured.hero_image_url || HERO_FALLBACK} alt={featured.hero_image_alt || featured.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-10">
                  <div className="mb-2 flex flex-wrap gap-1 text-[11px]">
                    {(featured.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-700">{t}</span>
                    ))}
                  </div>
                  <h2 className="font-display text-2xl font-extrabold leading-tight text-navy group-hover:text-brandblue sm:text-3xl">
                    {featured.title}
                  </h2>
                  {featured.subtitle && <p className="mt-2 text-base text-slate-500">{featured.subtitle}</p>}
                  <div className="mt-4 flex items-center gap-3 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {featured.read_minutes || 3} min</span>
                    {featured.author && <span>· {featured.author}</span>}
                    <span className="ml-auto">{new Date(featured.published_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}</span>
                  </div>
                </div>
              </Link>
            )}
            {rest.length > 0 && (
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
                {rest.map((p) => <Card key={p.slug} p={p} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blog;
